/**
 * 物件リクエストフォーム（#property-request-form）のクライアント側バリデーション
 * document-request-form-validation.js と同様の visit__fieldError / is-invalid で表示する
 */
import { isValidPhoneTel, PHONE_TEL_FORMAT_MESSAGE } from './phone-validation.js';
import { isValidEmail } from './email-validation.js';
import { isRangePlaceholderValue } from './range-select-control.js';
const ZIP_RE = /^\d{3}-?\d{4}$/;

function trimValue(id) {
	const el = document.getElementById(id);
	return el ? el.value.trim() : '';
}

function clearErrorForControl(form, controlId) {
	form.querySelectorAll(`.visit__fieldError[data-for="${controlId}"]`).forEach((n) => n.remove());
	const control = document.getElementById(controlId);
	if (!control) return;
	control.removeAttribute('aria-invalid');
	control.classList.remove('is-invalid');
	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) wrap.classList.remove('is-invalid');
	const schedule = control.closest('.visit__schedule');
	if (schedule) schedule.classList.remove('is-invalid');
	const consent = control.closest('.visit__consent');
	if (consent) consent.classList.remove('is-invalid');
	if (controlId === 'preq-budget-min' || controlId === 'preq-budget-max') {
		control.closest('.propertyRequest__budgetRow')?.querySelectorAll('select').forEach((select) => {
			select.classList.remove('is-invalid');
			select.removeAttribute('aria-invalid');
		});
	}
}

function clearPropertyRequestFormErrors(form) {
	form.querySelectorAll('.visit__fieldError').forEach((n) => n.remove());
	form.querySelectorAll('.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__inputWrap--date.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__schedule.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__consent.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('[aria-invalid="true"]').forEach((n) => n.removeAttribute('aria-invalid'));
}

function mountErrorParagraph(controlId, message) {
	const p = document.createElement('p');
	p.className = 'visit__fieldError';
	p.setAttribute('data-for', controlId);
	p.setAttribute('role', 'alert');
	p.textContent = message;
	return p;
}

function showFieldError(form, controlId, message) {
	const control = document.getElementById(controlId);
	if (!control) return;

	clearErrorForControl(form, controlId);

	control.setAttribute('aria-invalid', 'true');
	control.classList.add('is-invalid');

	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) {
		wrap.classList.add('is-invalid');
		wrap.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	const schedule = control.closest('.visit__schedule');
	if (schedule) {
		schedule.classList.add('is-invalid');
		schedule.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	if (control.id === 'preq-name-family' || control.id === 'preq-name-given') {
		const grid = control.closest('.visit__fieldGrid--name');
		if (grid) {
			grid.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
			return;
		}
	}

	if (control.id === 'preq-budget-min' || control.id === 'preq-budget-max') {
		const budgetRow = control.closest('.propertyRequest__budgetRow');
		if (budgetRow) {
			budgetRow.querySelectorAll('select').forEach((select) => {
				select.setAttribute('aria-invalid', 'true');
				select.classList.add('is-invalid');
			});
			budgetRow.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
			return;
		}
	}

	if (control instanceof HTMLFieldSetElement && (control.id === 'preq-type-fieldset' || control.id === 'preq-move-fieldset')) {
		control.appendChild(mountErrorParagraph(controlId, message));
		return;
	}

	control.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
}

function focusFirst(controlId) {
	const el = document.getElementById(controlId);
	if (!el) return;
	if (el instanceof HTMLFieldSetElement) {
		const first =
			el.querySelector('input[type="checkbox"]') || el.querySelector('input[type="radio"]');
		if (first instanceof HTMLElement) {
			first.focus();
		}
		return;
	}
	const schedule = el.closest('.visit__schedule');
	if (schedule instanceof HTMLElement) {
		const focusable = schedule.querySelector('.visit__scheduleArrow, .visit__scheduleSlot');
		if (focusable instanceof HTMLElement) {
			focusable.focus();
			return;
		}
	}
	const fpRoot = el.closest('.flatpickr');
	if (fpRoot) {
		const alt = fpRoot.querySelector('.flatpickr-alt-input');
		(alt || el).focus();
		return;
	}
	el.focus();
}

function budgetNumeric(value) {
	if (isRangePlaceholderValue(value) || value === 'none') return null;
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
}

/**
 * @returns {boolean}
 */
export function validatePropertyRequestForm(form) {
	clearPropertyRequestFormErrors(form);

	const errors = [];

	const typeChecked = form.querySelectorAll('input[name="preq_type[]"]:checked');
	if (!typeChecked.length) {
		errors.push(['preq-type-fieldset', '物件種別を1つ以上選択してください。']);
	}

	const area = trimValue('preq-area');
	if (!area) {
		errors.push(['preq-area', 'エリアを入力してください。']);
	}

	const minV = trimValue('preq-budget-min');
	const maxV = trimValue('preq-budget-max');
	if (isRangePlaceholderValue(minV) || isRangePlaceholderValue(maxV)) {
		errors.push(['preq-budget-min', 'ご予算を選択してください。']);
	} else {
		const minN = budgetNumeric(minV);
		const maxN = budgetNumeric(maxV);
		if (minN !== null && maxN !== null && minN >= maxN) {
			errors.push(['preq-budget-min', 'ご予算の下限が上限を超えています。']);
		}
	}

	const family = trimValue('preq-name-family');
	const given = trimValue('preq-name-given');
	if (!family && !given) {
		errors.push(['preq-name-family', '性・名を入力してください。']);
	} else if (!family) {
		errors.push(['preq-name-family', '性を入力してください。']);
	} else if (!given) {
		errors.push(['preq-name-given', '名を入力してください。']);
	}

	const telRaw = trimValue('preq-tel');
	if (telRaw && !isValidPhoneTel(telRaw)) {
		errors.push(['preq-tel', PHONE_TEL_FORMAT_MESSAGE]);
	}

	const email = trimValue('preq-email');
	if (!email) {
		errors.push(['preq-email', 'メールアドレスを入力してください。']);
	} else if (!isValidEmail(email)) {
		errors.push(['preq-email', 'メールアドレスの形式が正しくありません。']);
	}

	const zipRaw = trimValue('preq-zip');
	const zipClean = zipRaw.replace(/[〒\s]/g, '');
	if (zipRaw && !ZIP_RE.test(zipClean)) {
		errors.push(['preq-zip', '郵便番号は7桁の半角数字で入力してください（ハイフン可）。']);
	}

	if (errors.length === 0) {
		return true;
	}

	for (const [id, msg] of errors) {
		showFieldError(form, id, msg);
	}
	focusFirst(errors[0][0]);
	return false;
}

export function initPropertyRequestFormValidation(form) {
	if (!form || form.id !== 'property-request-form') return;

	const onEdit = (e) => {
		const t = e.target;
		if (!t || !form.contains(t)) return;
		if (t.id) {
			clearErrorForControl(form, t.id);
		}
		if (t.matches?.('input[name="preq_type[]"]')) {
			clearErrorForControl(form, 'preq-type-fieldset');
		}
		if (t.matches?.('input[name="preq_move_in"]')) {
			clearErrorForControl(form, 'preq-move-fieldset');
		}
		if (t.id === 'preq-budget-min' || t.id === 'preq-budget-max') {
			clearErrorForControl(form, 'preq-budget-min');
			clearErrorForControl(form, 'preq-budget-max');
		}
	};

	form.addEventListener('input', onEdit);
	form.addEventListener('change', onEdit);
}
